import type { DashboardExtra } from '@qlk/shared';

/* Nhập thẳng từ `ui/Money.js`, không qua `ui/index.js`: file này là hàm thuần
   và test của nó không nên phải nạp cả bộ thành phần React. */
import { dinhDangTien } from '../../../ui/Money.js';

/**
 * Dựng chuỗi "Doanh thu gộp đi về đâu" của khối Dòng tiền — **hàm thuần**, để
 * bất biến quan trọng nhất của khối được kiểm bằng test đơn vị chứ không bằng
 * mắt: *tổng các đoạn trên thanh bằng doanh thu gộp*.
 *
 * File này **không có công thức tiền nào**. Mọi con số đã do máy chủ tính
 * (`/dashboard/them`: `chuoiTien` từ enricher `revenueSummary`, `chiaNhanSu` từ
 * enricher `chiaSe`); ở đây chỉ xếp chúng thành dòng, tính **tỷ trọng trên
 * gộp** và quyết định đoạn nào được vẽ.
 *
 * ## Ba bản, chọn bằng cờ của máy chủ
 *
 * `chia.xemDayDu` quyết định, giao diện **không** tự suy quyền (xem `docs/ui.md`
 * mục 16). Người xem hẹp không có `chiaNhanSu` / `congTyGiuLai` trong JSON, nên
 * bản của họ dừng ở "Phần của bạn" và phần còn lại của lợi nhuận gọi đúng tên
 * là "Lợi nhuận còn lại" — không phải "Công ty giữ lại", vì số đó còn chứa phần
 * của những người khác mà họ không được xem. Bản thứ ba (`chi-thuc-nhan`) dành
 * cho lúc module Nhân sự không trả được phần chia: chuỗi dừng ở "Thực nhận".
 *
 * ## Vì sao thuế Mỹ có trong **mọi** bản
 *
 * Bản demo chỉ kể thuế Mỹ ở bản quản trị vì kỳ đó thuế bằng 0. Nhưng thanh đo
 * tỷ trọng **trên gộp**: bỏ một khoản trừ ra khỏi chuỗi là thanh thiếu một
 * đoạn mà không ai biết đoạn đó là gì. Thuế Mỹ cũng không phải số nhạy cảm —
 * người bị giới hạn kênh đã thấy nó ở khối "Dòng tiền" của trang Doanh thu.
 */

/** Khoá của một bước; dùng làm `key` React và trong test. */
export type KhoaBuocTien =
  | 'thueMy'
  | 'phanNetwork'
  | 'vat'
  | 'thucNhan'
  | 'chiPhi'
  | 'chiaNhanSu'
  | 'congTyGiuLai'
  | 'loiNhuan'
  | 'loiNhuanConLai'
  | 'phanCuaToi';

/**
 * Vai của một dòng trong danh sách bước:
 *
 * - `tru` — một khoản bị trừ khỏi gộp, luôn có đoạn trên thanh;
 * - `moc` — **mốc cộng dồn** (Thực nhận, Lợi nhuận kênh): không có đoạn riêng,
 *   nó bằng tổng các đoạn còn lại ở bên phải nó;
 * - `cuoi` — dòng kết của chuỗi, in đậm và có đoạn.
 */
export type VaiBuocTien = 'tru' | 'moc' | 'cuoi';

export interface BuocTien {
  khoa: KhoaBuocTien;
  nhan: string;
  /** Chú thích ngắn bên phải nhãn; `undefined` = không có. */
  ghiChu?: string;
  giaTri: number;
  /** Tỷ trọng trên doanh thu gộp, 0–100. `0` khi gộp bằng 0. */
  pct: number;
  vai: VaiBuocTien;
  /** Token màu của đoạn; `undefined` với dòng `moc`. */
  mau?: string;
  /**
   * Dòng này có vẽ đoạn trên thanh hay không.
   *
   * `false` khi là mốc cộng dồn, **và** khi giá trị ≤ 0: thuế Mỹ 0% vẫn phải có
   * dòng (để không ai tưởng app quên trừ), nhưng một đoạn rộng 0px chỉ thêm một
   * đường kẻ lạ; còn lợi nhuận âm thì không có "đoạn âm" nào vẽ được.
   */
  veDoan: boolean;
}

export interface DoanThanh {
  khoa: KhoaBuocTien;
  nhan: string;
  giaTri: number;
  pct: number;
  mau: string;
}

export interface ChuoiDongTien {
  gross: number;
  currency: string;
  /** Mọi bước của chuỗi, theo đúng thứ tự trừ. */
  buoc: BuocTien[];
  /** Chỉ các bước thật sự vẽ đoạn, cùng thứ tự. */
  doan: DoanThanh[];
  /** Nhãn cho trình đọc màn hình của thanh. */
  nhanThanh: string;
  /**
   * `gross − Σ đoạn`. Bằng 0 ở mọi kỳ bình thường.
   *
   * Khác 0 khi phần chia của kỳ lấy từ **ảnh chụp lúc chốt** (tháng đã chốt)
   * mà doanh thu nguồn đã bị sửa sau đó: hai nửa chuỗi khi ấy nói về hai thời
   * điểm. Giao diện ghi rõ chênh lệch thay vì kéo một đoạn cho khít.
   */
  lech: number;
  /** `false` khi kỳ không có doanh thu ⇒ khối hiện `—` và thanh trống. */
  coSoLieu: boolean;
  /**
   * Chuỗi dài tới đâu:
   *
   * - `day-du` — tới "Công ty giữ lại" (người xem phần chia đầy đủ);
   * - `hep` — tới "Phần của bạn" (người xem hẹp);
   * - `chi-thuc-nhan` — dừng ở "Thực nhận", vì module Nhân sự không trả được
   *   phần chia của **mọi** kênh trong kỳ. Nửa chuỗi vẫn đúng và vẫn đáng bày;
   *   một tổng thiếu vài kênh thì không (xem `DashboardExtra.chiaNhanSu`).
   */
  che: 'day-du' | 'hep' | 'chi-thuc-nhan';
}

export interface DauVaoDongTien {
  chuoi: DashboardExtra['chuoiTien'] | undefined;
  chia: NonNullable<DashboardExtra['chiaNhanSu']> | null | undefined;
  /** Tỷ lệ của người đang xem, để ghi "Phần của bạn · 7%". `null` = không ghi. */
  pctCuaToi?: number | null;
}

/** Cent, để so sánh và cộng không lệ thuộc dấu phẩy động. */
function cent(x: number): number {
  return Math.round(x * 100);
}

function tien(cents: number): number {
  return cents / 100;
}

/** Tỷ trọng trên gộp, một chữ số thập phân. Gộp 0 ⇒ 0. */
function tyTrong(giaTri: number, gross: number): number {
  if (cent(gross) === 0) return 0;
  return Math.round((giaTri / gross) * 1000) / 10;
}

export function phanTramGon(pct: number): string {
  return `${pct.toLocaleString('vi-VN', { maximumFractionDigits: 1 })}%`;
}

interface BuocTho {
  khoa: KhoaBuocTien;
  nhan: string;
  ghiChu?: string;
  giaTri: number;
  vai: VaiBuocTien;
  mau?: string;
}

/**
 * Chuỗi rỗng dùng cho kỳ không có doanh thu. Không trả `null`: giao diện vẫn
 * cần `currency` và vẫn vẽ khung thanh (trống) để bố cục không nhảy.
 */
function chuoiRong(currency: string, che: ChuoiDongTien['che']): ChuoiDongTien {
  return {
    gross: 0,
    currency,
    buoc: [],
    doan: [],
    nhanThanh: 'Không có số liệu trong kỳ này',
    lech: 0,
    coSoLieu: false,
    che,
  };
}

export function dungChuoiDongTien(dau: DauVaoDongTien): ChuoiDongTien {
  const { chuoi, chia } = dau;
  const currency = chuoi?.currency ?? 'USD';

  /*
   * Phần chia thiếu thì chuỗi **dừng ở Thực nhận** chứ không biến mất: nửa trên
   * (thuế, Network, VAT) nằm ở `chuoiTien` và vẫn đúng. Đó cũng là nửa mà bản
   * trước của trang đã bày bằng hai ô rời "Công ty thực nhận" và "Phần chia
   * Network" — module Nhân sự lỗi thì không được lấy luôn hai số đó đi.
   */
  const che: ChuoiDongTien['che'] =
    chia === null || chia === undefined ? 'chi-thuc-nhan' : chia.xemDayDu ? 'day-du' : 'hep';

  if (!chuoi || cent(chuoi.gross) <= 0) return chuoiRong(currency, che);

  const gross = chuoi.gross;

  const dauChuoi: BuocTho[] = [
    {
      khoa: 'thueMy',
      nhan: 'Thuế Mỹ',
      ghiChu: 'Google giữ, trên phần doanh thu ở Mỹ',
      giaTri: chuoi.thueMy,
      vai: 'tru',
      mau: 'var(--color-doan-thue)',
    },
    {
      khoa: 'phanNetwork',
      nhan: 'Phần Network',
      ghiChu: 'MCN giữ, sau thuế Mỹ',
      giaTri: chuoi.phanNetwork,
      vai: 'tru',
      mau: 'var(--color-doan-network)',
    },
    {
      khoa: 'vat',
      nhan: 'VAT',
      ghiChu: 'trên phần công ty nhận',
      giaTri: chuoi.vat,
      vai: 'tru',
      mau: 'var(--color-doan-vat)',
    },
  ];

  /*
   * "Thực nhận" là **mốc cộng dồn** khi chuỗi còn đi tiếp, nhưng là **dòng kết**
   * khi chuỗi dừng ở đó — và dòng kết thì phải có đoạn, nếu không thanh hụt
   * đúng phần lớn nhất của nó.
   */
  const cuoiChuoi: BuocTho[] =
    che === 'chi-thuc-nhan'
      ? [
          {
            khoa: 'thucNhan',
            nhan: 'Thực nhận',
            ghiChu: 'chưa có phần chia của kỳ này',
            giaTri: chuoi.thucNhan,
            vai: 'cuoi',
            mau: 'var(--color-doan-giu-lai)',
          },
        ]
      : [
          { khoa: 'thucNhan', nhan: 'Thực nhận', giaTri: chuoi.thucNhan, vai: 'moc' },
          {
            khoa: 'chiPhi',
            nhan: 'Chi phí vận hành',
            giaTri: chia?.chiPhiVanHanh ?? 0,
            vai: 'tru',
            mau: 'var(--color-doan-chi-phi)',
          },
          ...(chia === null || chia === undefined ? [] : duoiThucNhan(che, chia, dau.pctCuaToi)),
        ];

  const buoc: BuocTien[] = [...dauChuoi, ...cuoiChuoi].map((b) => ({
    ...b,
    pct: tyTrong(b.giaTri, gross),
    /* Mốc cộng dồn không có đoạn; khoản ≤ 0 có dòng nhưng không có đoạn. */
    veDoan: b.vai !== 'moc' && cent(b.giaTri) > 0,
  }));

  const doan: DoanThanh[] = buoc
    .filter((b) => b.veDoan)
    .map((b) => ({ khoa: b.khoa, nhan: b.nhan, giaTri: b.giaTri, pct: b.pct, mau: b.mau ?? '' }));

  const lech = tien(cent(gross) - doan.reduce((t, d) => t + cent(d.giaTri), 0));

  return {
    gross,
    currency,
    buoc,
    doan,
    nhanThanh: `Doanh thu gộp ${dinhDangTien(gross, currency === 'VND' ? 'VND' : 'USD')} chia thành: ${doan
      .map((d) => `${d.nhan} ${phanTramGon(d.pct)}`)
      .join(', ')}`,
    lech,
    coSoLieu: true,
    che,
  };
}

/** Ba bước sau "Chi phí vận hành", khác nhau theo luật che của máy chủ. */
function duoiThucNhan(
  che: ChuoiDongTien['che'],
  chia: NonNullable<DashboardExtra['chiaNhanSu']>,
  pctCuaToi: number | null | undefined,
): BuocTho[] {
  if (che === 'day-du') {
    return [
      {
        khoa: 'chiaNhanSu',
        nhan: 'Chia nhân sự',
        giaTri: chia.chiaNhanSu ?? 0,
        vai: 'tru',
        mau: 'var(--color-doan-nhan-su)',
      },
      {
        khoa: 'congTyGiuLai',
        nhan: 'Công ty giữ lại',
        giaTri: chia.congTyGiuLai ?? 0,
        vai: 'cuoi',
        mau: 'var(--color-doan-giu-lai)',
      },
    ];
  }

  /*
   * Bản người xem hẹp. "Lợi nhuận còn lại" **không** được gọi là "Công ty giữ
   * lại": số đó còn chứa phần của những người khác, mà tên người và tiền của họ
   * không đi qua mạng tới đây.
   */
  return [
    { khoa: 'loiNhuan', nhan: 'Lợi nhuận kênh', giaTri: chia.loiNhuan, vai: 'moc' },
    {
      khoa: 'loiNhuanConLai',
      nhan: 'Lợi nhuận còn lại',
      ghiChu: 'sau phần của bạn',
      /* Qua cent: `646.92 - 50.5` trên `number` ra `596.4200000000001`. */
      giaTri: tien(cent(chia.loiNhuan) - cent(chia.phanCuaToi)),
      vai: 'tru',
      mau: 'var(--color-doan-nhan-su)',
    },
    {
      khoa: 'phanCuaToi',
      nhan: 'Phần của bạn',
      ...(pctCuaToi === null || pctCuaToi === undefined
        ? {}
        : { ghiChu: `${phanTramGon(pctCuaToi)} lợi nhuận kênh` }),
      giaTri: chia.phanCuaToi,
      vai: 'cuoi',
      mau: 'var(--color-doan-cua-toi)',
    },
  ];
}

/** Một bước theo khoá, để trang lấy con số chính mà không lặp lại điều kiện. */
export function buocTheoKhoa(c: ChuoiDongTien, khoa: KhoaBuocTien): BuocTien | undefined {
  return c.buoc.find((b) => b.khoa === khoa);
}
